<script setup>
import { ref, computed, getCurrentInstance, onMounted, reactive } from 'vue';
import { router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useCurrencyFormatter } from '../../../../utils/currencyFormatter';


const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const $filters = proxy.$filters;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();
const { formatConvertedPrice, getCurrentCurrency, onCurrencyChange, exchangeRates } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());


// TODO: coinStore mock — coinFormat is unused, remove both when confirmed safe
const coinStore = reactive({
    coin: 'USD',
    ratios: {
        'BRL': 1,
        'EUR': 1,
        'USD': 1,
        'GBP': 1
    }
})

const props = defineProps({
    seller: Object,
    sites: Object,
    coins: Object,
    apiKey: String,
    apiUsage: Object,
});

const apiKeyVisible = ref(false);
const maskedApiKey = computed(() => {
    if (!props.apiKey) return '-';
    return '••••••••••••••••••••••••••••••••';
});

// State
const selectedSites = ref([]);
const linkCopied = ref(false);
const apiKeyCopied = ref(false);

// Computed
const links = computed(() => {
    if (!props.sites?.links) return [];
    const _links = [...props.sites.links];
    _links.shift();
    _links.pop();
    return _links;
});

const coinFormat = computed(() => props.coins?.[coinStore.coin] || {});

// Copy API key
function copyApiKey() {
    if (!props.apiKey) return;
    navigator.clipboard.writeText(props.apiKey).then(() => {
        apiKeyCopied.value = true;
        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('API Key copied to clipboard'),
            life: 2000
        });
        setTimeout(() => {
            apiKeyCopied.value = false;
        }, 2000);
    }, () => {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to copy API Key'),
            life: 3000
        });
    });
}

// Copy seller referral link
function copySellferLink() {
    const url = route('submitSite.create', {
        _query: {
            ref: props.seller.email,
        },
    });

    navigator.clipboard.writeText(url).then(() => {
        linkCopied.value = true;
        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Link successfully copied to clipboard'),
            life: 2000
        });
        setTimeout(() => {
            linkCopied.value = false;
        }, 2000);
    }, () => {
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: $t('Failed to copy link'),
            life: 3000
        });
    });
}

// Navigation
function goBack() {
    showLoading('Loading sellers...');
    router.visit(route('sellers.index'), { onFinish: () => hideLoading() });
}

function editSeller() {
    showLoading('Loading seller edit form...');
    router.visit(route('sellers.edit', props.seller.id), { onFinish: () => hideLoading() });
}

function editSite(site) {
    showLoading('Loading site edit form...');
    router.visit(route('sites.edit', site.id), { onFinish: () => hideLoading() });
}


const isSponsorSite = (site) => {
    return site.link_type === 'SPONSOR' ||
           site.link_type === 'NOFOLLOW_AND_SPONSOR' ||
           site.link_type === 'DOFOLLOW_AND_SPONSOR' ||
           site.link_type === 'NOFOLLOW_DOFOLLOW_SPONSOR';
};

const typeIconsMap = {
    'casinos': 'pi pi-box',
    'cbd': 'pi pi-building-columns',
    'crypto': 'pi pi-bitcoin',
    'dating': 'pi pi-heart',
    'erotic': 'pi pi-ban',
    'bet': 'pi pi-dollar',
    'loan': 'pi pi-credit-card',
    'forex': 'pi pi-chart-bar',
    'default': 'pi pi-circle',
    'none': 'pi pi-minus-circle',
    'newtype': 'pi pi-plus-circle',
    '_fallback': 'pi pi-tag'
};

const getTypeIcon = (type) => {
    if (!type || !type.name) return typeIconsMap._fallback;
    const typeName = type.name.toLowerCase().trim();
    if (typeName === 'default') return null;
    return type.icon_name || typeIconsMap[typeName] || typeIconsMap._fallback;
};

const FIXED_NICHE_ORDER = ['casinos', 'cbd', 'crypto', 'dating', 'erotic', 'bet', 'loan', 'forex'];
const NICHE_DISPLAY_LIMIT = 10;

const getAcceptedNiches = (site) => {
    if (!site.unique_offers_by_type || !Array.isArray(site.unique_offers_by_type)) {
        return [];
    }

    const niches = site.unique_offers_by_type.map(offer => {
        const icon = getTypeIcon(offer.type);

        return {
            id: offer.id,
            type: offer.type.name.toLowerCase(),
            icon: icon,
            label: offer.type.name,
            price: offer.converted_price,
            currency: offer.converted_coin,
            original_price: offer.original_price,
            original_currency: offer.original_coin,
            isDefault: offer.type.name.toLowerCase() === 'default'
        };
    });

    niches.sort((a, b) => {
        const ai = FIXED_NICHE_ORDER.indexOf(a.type);
        const bi = FIXED_NICHE_ORDER.indexOf(b.type);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.type.localeCompare(b.type);
    });

    return niches;
};

const getVisibleNiches = (site) => getAcceptedNiches(site).slice(0, NICHE_DISPLAY_LIMIT);
const getExtraNichesCount = (site) => Math.max(0, getAcceptedNiches(site).length - NICHE_DISPLAY_LIMIT);
const getExtraNichesTooltip = (site) => getAcceptedNiches(site).slice(NICHE_DISPLAY_LIMIT).map(n => n.label).join(', ');

const getNichePriceTooltip = (niche) => {
    if (!niche.price) return niche.label;
    const formattedPrice = formatConvertedPrice(niche.price, niche.currency);
    return `${niche.label}: ${formattedPrice}`;
};

const getBestSellerPrice = (site) => {
    const niches = getAcceptedNiches(site);
    if (niches.length === 0) return '--';

    let bestPrice = 0;
    let bestCurrency = 'USD';
    let bestPriceUSD = 0;

    niches.forEach(niche => {
        if (niche.price) {
            const fromRate = exchangeRates.value[niche.currency] || 100;
            const priceInUSD = (niche.price / fromRate) * 100;

            if (priceInUSD > bestPriceUSD) {
                bestPriceUSD = priceInUSD;
                bestPrice = niche.price;
                bestCurrency = niche.currency;
            }
        }
    });

    if (bestPrice === 0) return '--';

    return formatConvertedPrice(bestPrice, bestCurrency);
};

// API Usage chart data
const apiChartData = computed(() => {
    if (!props.apiUsage?.daily_counts) return null;

    const labels = [];
    const data = [];
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = d.toISOString().split('T')[0];
        labels.push(`${d.getDate()}/${d.getMonth() + 1}`);
        data.push(props.apiUsage.daily_counts[key] || 0);
    }

    return {
        labels,
        datasets: [{
            label: 'Requests',
            data,
            backgroundColor: '#3b82f6',
            borderRadius: 3,
            maxBarThickness: 14,
        }],
    };
});

const apiChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    barPercentage: 0.8,
    categoryPercentage: 0.9,
    plugins: {
        legend: { display: false },
        tooltip: {
            callbacks: {
                label: (item) => `${item.raw} requests`,
            },
        },
    },
    scales: {
        x: {
            grid: { display: false },
            ticks: {
                maxRotation: 0,
                font: { size: 10 },
            },
        },
        y: {
            display: false,
            beginAtZero: true,
        },
    },
};

const formatLastAccess = computed(() => {
    if (!props.apiUsage?.last_access) return null;
    const d = new Date(props.apiUsage.last_access.date);
    return d.toLocaleString();
});

defineOptions({
    layout: AdminMainLayout,
});


onMounted(() => {
    onCurrencyChange(({ currency }) => {
        currentCurrency.value = currency;
    });

    currentCurrency.value = getCurrentCurrency();
});
</script>

<template>
    <div>
        <!-- Header Card -->
        <Card class="mb-6">
            <template #content>
                <div class="flex justify-between items-center">
                    <div class="flex items-center gap-3">
                        <Button
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            text
                            rounded
                            @click="goBack"
                            v-tooltip.top="$t('Back to list')"
                        />
                        <div class="flex items-center gap-3">
                            <Avatar
                                :label="seller.name?.charAt(0)?.toUpperCase() || 'S'"
                                size="large"
                                shape="circle"
                                :style="{ backgroundColor: '#2563eb', color: 'white' }"
                            />
                            <div>
                                <h2 class="text-xl font-bold">{{ seller.name }}</h2>
                                <span class="text-sm text-surface-500">{{ $t('Seller details') }}</span>
                            </div>
                        </div>
                    </div>
                    <Button
                        :label="$t('Edit seller')"
                        icon="pi pi-pencil"
                        severity="primary"
                        @click="editSeller"
                    />
                </div>
            </template>
        </Card>

        <!-- Seller Details Card -->
        <Card class="mb-6">
            <template #title>
                <div class="flex items-center gap-2">
                    <i class="pi pi-user text-primary-500"></i>
                    {{ $t('Seller Information') }}
                </div>
            </template>
            <template #content>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('Username') }}</label>
                        <div class="flex items-center gap-2">
                            <i class="pi pi-user text-surface-400"></i>
                            <span class="font-medium">{{ seller.name || '-' }}</span>
                        </div>
                    </div>

                    <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('Email') }}</label>
                        <div class="flex items-center gap-2">
                            <i class="pi pi-envelope text-surface-400"></i>
                            <span class="font-medium">{{ seller.email || '-' }}</span>
                        </div>
                    </div>

                    <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('Phone') }}</label>
                        <div class="flex items-center gap-2">
                            <i class="pi pi-phone text-surface-400"></i>
                            <span class="font-medium">{{ seller.phone || '-' }}</span>
                        </div>
                    </div>

                    <!-- <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('Commission') }}</label>
                        <div class="flex items-center gap-2">
                            <span class="font-medium">
                                {{
                                    formatCommission(seller)
                                }}
                            </span>
                        </div>
                    </div> -->

                    <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('Created at') }}</label>
                        <div class="flex items-center gap-2">
                            <i class="pi pi-calendar text-surface-400"></i>
                            <span class="font-medium">{{ new Date(seller.created_at).toLocaleDateString() }}</span>
                        </div>
                    </div>

                    <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('Account ID') }}</label>
                        <div class="flex items-center gap-2">
                            <i class="pi pi-hashtag text-surface-400"></i>
                            <span class="font-medium font-mono">{{ seller.id }}</span>
                        </div>
                    </div>

                    <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('API Key') }}</label>
                        <div class="flex gap-2">
                            <InputText
                                :value="apiKeyVisible ? apiKey : maskedApiKey"
                                readonly
                                class="flex-1 font-mono text-sm"
                            />
                            <Button
                                :icon="apiKeyVisible ? 'pi pi-eye-slash' : 'pi pi-eye'"
                                severity="secondary"
                                text
                                rounded
                                @click="apiKeyVisible = !apiKeyVisible"
                                v-tooltip.top="apiKeyVisible ? $t('Hide') : $t('Show')"
                            />
                            <Button
                                :icon="apiKeyCopied ? 'pi pi-check' : 'pi pi-copy'"
                                :severity="apiKeyCopied ? 'success' : 'secondary'"
                                text
                                rounded
                                :disabled="!apiKey"
                                @click="copyApiKey"
                                v-tooltip.top="$t('Copy API Key')"
                            />
                        </div>
                        <a
                            href="/api-reference"
                            target="_blank"
                            class="mt-1 inline-flex items-center gap-1 text-xs text-primary-500 hover:underline"
                        >
                            <i class="pi pi-external-link" style="font-size:0.7rem;"></i>
                            {{ $t('View API Reference') }}
                        </a>
                    </div>
                </div>

                <div class="mt-6 pt-6 border-t">
                    <label class="text-sm text-surface-500 mb-2 block">{{ $t('Seller referral link') }}</label>
                    <div class="flex gap-2">
                        <InputText
                            :value="route('submitSite.create', { _query: { ref: seller.email } })"
                            readonly
                            class="flex-1 font-mono text-sm"
                        />
                        <Button
                            :icon="linkCopied ? 'pi pi-check' : 'pi pi-copy'"
                            :label="linkCopied ? $t('Copied!') : $t('Copy seller link')"
                            :severity="linkCopied ? 'success' : 'primary'"
                            @click="copySellferLink"
                        />
                    </div>
                </div>
            </template>
        </Card>

        <!-- API Usage Card -->
        <Card v-if="apiUsage" class="mb-6">
            <template #title>
                <div class="flex items-center gap-2">
                    <i class="pi pi-chart-bar text-primary-500"></i>
                    {{ $t('API Usage') }}
                    <Tag :value="`${apiUsage.total_30d || 0} requests (30d)`" severity="info" />
                </div>
            </template>
            <template #content>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('Total requests') }} (30d)</label>
                        <div class="flex items-center gap-2">
                            <i class="pi pi-send text-surface-400"></i>
                            <span class="font-medium text-xl">{{ apiUsage.total_30d || 0 }}</span>
                        </div>
                    </div>

                    <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('Last access') }}</label>
                        <div class="flex items-center gap-2" v-if="apiUsage.last_access">
                            <i class="pi pi-clock text-surface-400"></i>
                            <span class="font-medium">{{ formatLastAccess }}</span>
                        </div>
                        <span v-else class="text-surface-400">-</span>
                    </div>

                    <div class="field">
                        <label class="text-sm text-surface-500 mb-1 block">{{ $t('Last IP') }}</label>
                        <div class="flex items-center gap-2" v-if="apiUsage.last_access">
                            <i class="pi pi-globe text-surface-400"></i>
                            <span class="font-medium font-mono">{{ apiUsage.last_access.ip }}</span>
                        </div>
                        <span v-else class="text-surface-400">-</span>
                    </div>
                </div>

                <div class="mt-4 pt-4 border-t" v-if="apiChartData">
                    <label class="text-sm text-surface-500 mb-2 block">{{ $t('Requests per day') }} (30d)</label>
                    <div style="height: 180px;">
                        <Chart type="bar" :data="apiChartData" :options="apiChartOptions" style="height: 100%;" />
                    </div>
                </div>
            </template>
        </Card>

        <!-- Sites Card -->
        <Card>
            <template #title>
                <div class="flex justify-between items-center">
                    <div class="flex items-center gap-2">
                        <i class="pi pi-globe text-primary-500"></i>
                        {{ $t('Sites') }}
                        <Tag :value="`${sites?.total || 0} ${$t('total')}`" severity="secondary" />
                    </div>
                </div>
            </template>
            <template #content>
                <DataTable
                    v-model:selection="selectedSites"
                    :value="sites?.data || []"
                    dataKey="id"
                    :paginator="false"
                    responsiveLayout="scroll"
                    :scrollable="true"
                    scrollHeight="600px"
                >
                    <template #empty>
                        <div class="text-center py-8">
                            <i class="pi pi-inbox text-4xl text-surface-400 mb-4"></i>
                            <p class="text-surface-500">{{ $t('No sites found for this seller') }}</p>
                        </div>
                    </template>

                    <Column field="url" :header="$t('Domain')" style="min-width: 200px">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2">
                                <Tag
                                    v-if="slotProps.data.deleted_at"
                                    icon="pi pi-exclamation-triangle"
                                    severity="danger"
                                    :value="$t('Deleted')"
                                    class="text-xs"
                                />
                                <a
                                    :href="`https://${slotProps.data.url}`"
                                    target="_blank"
                                    class="text-primary-500 hover:text-primary-700 flex items-center gap-1"
                                >
                                    {{ slotProps.data.url }}
                                    <i class="pi pi-external-link text-xs"></i>
                                </a>
                            </div>
                        </template>
                    </Column>

                    <Column field="sale" :header="$t('Price Displayed')" style="min-width: 120px">
                        <template #body="slotProps">
                            <div class="flex items-center gap-1">
                                <span class="font-medium whitespace-nowrap">{{ formatConvertedPrice(slotProps.data.sale, slotProps.data.sale_coin) }}</span>
                            </div>
                        </template>
                    </Column>

                    <Column field="sale" :header="`${seller.name} ${$t('Highest Price')}`" style="min-width: 120px">
                        <template #body="slotProps">
                            <div class="flex items-center gap-1">
                                <span class="font-medium whitespace-nowrap">{{ getBestSellerPrice(slotProps.data) }}</span>
                            </div>
                        </template>
                    </Column>

                    <Column field="accepted_niches" :header="`${seller.name} ${$t('Accepted niches')}`" style="min-width: 140px">
                        <template #body="slotProps">
                            <div class="flex items-center gap-2 flex-wrap">
                                <template v-for="niche in getVisibleNiches(slotProps.data)" :key="niche.id">
                                    <i
                                        v-if="niche.icon"
                                        :class="niche.icon"
                                        class="text-lg cursor-pointer"
                                        v-tooltip.top="getNichePriceTooltip(niche)"
                                    ></i>
                                </template>
                                <Tag
                                    v-if="getExtraNichesCount(slotProps.data) > 0"
                                    :value="`+${getExtraNichesCount(slotProps.data)} ${$t('more')}`"
                                    severity="secondary"
                                    class="text-xs cursor-pointer"
                                    v-tooltip.top="getExtraNichesTooltip(slotProps.data)"
                                />
                                <span v-if="getAcceptedNiches(slotProps.data).length === 0" class="text-surface-400 text-sm">-</span>
                            </div>
                        </template>
                    </Column>

                    <Column field="da" :header="$t('DA')" style="min-width: 80px">
                        <template #body="slotProps">
                            <Tag :value="slotProps.data.da || '-'" severity="secondary" />
                        </template>
                    </Column>

                    <Column field="dr" :header="$t('DR')" style="min-width: 80px">
                        <template #body="slotProps">
                            <Tag :value="slotProps.data.dr || '-'" severity="secondary" />
                        </template>
                    </Column>

                    <Column field="sponsor" :header="$t('Marked as sponsored')" style="min-width: 160px">
                        <template #body="slotProps">
                            <Tag
                                :icon="isSponsorSite(slotProps.data) ? 'pi pi-check' : 'pi pi-times'"
                                :severity="isSponsorSite(slotProps.data) ? 'success' : 'danger'"
                                :value="$t(isSponsorSite(slotProps.data) ? 'Yes' : 'No')"
                            />
                        </template>
                    </Column>

                    <Column field="category" :header="$t('Category')" style="min-width: 150px">
                        <template #body="slotProps">
                            <span v-if="slotProps.data.category" v-tooltip.top="slotProps.data.category.subtitle">
                                {{ slotProps.data.category.title }}
                            </span>
                            <span v-else class="text-surface-400">-</span>
                        </template>
                    </Column>

                    <Column field="formatted_inserted_at" :header="$t('Upload date')" style="min-width: 120px">
                        <template #body="slotProps">
                            <span class="text-sm">{{ slotProps.data.formatted_inserted_at }}</span>
                        </template>
                    </Column>

                    <Column field="formatted_updated_at" :header="$t('Updated date')" style="min-width: 120px">
                        <template #body="slotProps">
                            <span class="text-sm">{{ slotProps.data.formatted_updated_at }}</span>
                        </template>
                    </Column>

                    <Column :header="$t('Actions')" style="min-width: 80px" frozen alignFrozen="right">
                        <template #body="slotProps">
                            <Button
                                icon="pi pi-pencil"
                                severity="secondary"
                                text
                                rounded
                                v-tooltip.top="$t('Edit site')"
                                @click="editSite(slotProps.data)"
                            />
                        </template>
                    </Column>
                </DataTable>

                <!-- Custom Pagination -->
                <div class="flex items-center justify-between mt-4" v-if="sites?.links">
                    <div>
                        <span class="text-sm text-surface-500">
                            {{ $t('Showing') }} {{ sites.from || 0 }} {{ $t('to') }} {{ sites.to || 0 }}
                            {{ $t('of') }} {{ sites.total || 0 }} {{ $t('sites') }}
                        </span>
                    </div>
                    <div class="flex gap-1">
                        <Button
                            v-if="sites.prev_page_url"
                            icon="pi pi-chevron-left"
                            severity="secondary"
                            text
                            @click="() => { showLoading('Loading previous page...'); router.visit(sites.prev_page_url, { onFinish: () => hideLoading() }); }"
                        />
                        <Button
                            v-for="link in links"
                            :key="link.label"
                            :label="link.label"
                            :severity="link.active ? 'primary' : 'secondary'"
                            :text="!link.active"
                            @click="() => { showLoading('Loading page...'); router.visit(link.url, { onFinish: () => hideLoading() }); }"
                            :disabled="!link.url"
                        />
                        <Button
                            v-if="sites.next_page_url"
                            icon="pi pi-chevron-right"
                            severity="secondary"
                            text
                            @click="() => { showLoading('Loading next page...'); router.visit(sites.next_page_url, { onFinish: () => hideLoading() }); }"
                        />
                    </div>
                </div>
            </template>
        </Card>
    </div>
</template>

